import React from 'react';
import { Link } from 'react-router-dom';

const About = () => {
  const values = [
    {
      icon: '🎯',
      title: 'Excellence in Education',
      description: 'We believe in providing world-class finance education that meets industry standards and prepares students for real-world challenges.'
    },
    {
      icon: '🤝',
      title: 'Industry Partnership',
      description: 'Strong relationships with leading financial institutions ensure our curriculum stays current and our graduates get placed.'
    },
    {
      icon: '📈',
      title: 'Career Success',
      description: 'Our ultimate goal is your career success. We measure our success by the achievements of our graduates in the finance industry.'
    },
    {
      icon: '💡',
      title: 'Innovation in Learning',
      description: 'We continuously innovate our teaching methods, incorporating latest technology and real-world case studies for effective learning.'
    }
  ];

  const milestones = [
    { year: '2020', title: 'The First Classroom', description: 'The journey began with hands-on teaching in equity research and financial modeling, working closely with students across multiple colleges' },
    { year: '2022', title: 'Scaling the Impact', description: 'What started as small classroom sessions scaled into structured programs, with growing student participation and repeat collaborations across campuses.' },
    { year: '2023', title: 'Building Academic Partnerships', description: 'Expanded to 10+ college partners, delivering finance training at scale and refining our cohort-based learning approach through continuous student feedback.' },
    { year: '2024', title: 'Broadening the Domain', description: 'Introduced specialized cohorts in Fixed Income Securities and Quantitative Finance, moving beyond equity research into multi-domain finance education.' },
    { year: '2025', title: 'Intlrr Takes Shape', description: 'Years of teaching, partnerships, and curriculum refinement came together under one formal identity — Intlrr.' }
  ];

  const team = [
    {
      name: 'Manas Mittal',
      role: 'Founder',
      background: 'Senior finance professional with experience across structured finance and debt capital markets.IIM Indore alumnus.',
      image: '/manas.png',
      linkedin: 'https://www.linkedin.com/in/manas-mittal-3b8ab1235',
      expertise: 'Structured Finance · Debt Markets · Credit Analysis'
    },
    
    {
      name: ' Aniket ',
      role: 'Mentor',
      background: 'Private credit and finance professional with experience across fund finance, asset-backed lending, and financial modeling.',
      image: '/aniket.jpg',
      linkedin: 'https://www.linkedin.com/in/aniket-vaslas-9bb470155?lipi=urn%3Ali%3Apage%3Ad_flagship3_profile_view_base_contact_details%3BfY6MlqMIRI2MWeMMZior3w%3D%3D',
      expertise: 'Equity Research · Financial Modeling · Quant Finance · Private Credit · Credit Analysis'
    }
  ];

  return (
    <div className="about-page" data-testid="about-page">
      {/* Hero Section */}
      <section className="hero" style={{ minHeight: '70vh' }}>
        <div className="container">
          <div className="grid grid-2 gap-xl items-center">
            <div className="hero-content animate-fade-in-up">
              <h1 className="heading-primary mb-lg">
                Empowering Finance <span style={{ color: 'var(--secondary)' }}>Careers</span>
              </h1>
              <p className="text-large mb-xl" style={{ color: 'var(--text-secondary)' }}>
                At Intlrr, we bridge the gap between academic finance knowledge and industry requirements through practical, 
                cohort-based learning programs led by industry experts.
              </p>
              <Link to="/programs" className="btn btn-primary btn-large" data-testid="explore-programs-about-btn">
                Explore Our Programs
              </Link>
            </div>
            
            <div>
              <img 
                src="https://images.unsplash.com/photo-1526628953301-3e589a6a8b74" 
                alt="About Intlrr"
                style={{ width: '100%', borderRadius: '1.5rem', boxShadow: 'var(--shadow-xl)' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section" style={{ background: 'var(--surface)' }}>
        <div className="container">
          <div className="grid grid-2 gap-xl">
            <div className="card">
              <div style={{ width: '80px', height: '80px', background: 'var(--secondary)', borderRadius: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
                <span style={{ fontSize: '2rem', color: 'white' }}>🎯</span>
              </div>
              <h2 className="heading-tertiary mb-lg">Our Mission</h2>
              <p className="text-base" style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                To democratize high-quality finance education by making it accessible, practical, and industry-relevant. 
                We strive to create a bridge between academic learning and professional requirements, ensuring our students 
                are job-ready from day one.
              </p>
            </div>
            
            <div className="card">
              <div style={{ width: '80px', height: '80px', background: 'var(--accent)', borderRadius: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
                <span style={{ fontSize: '2rem', color: 'white' }}>🔮</span>
              </div>
              <h2 className="heading-tertiary mb-lg">Our Vision</h2>
              <p className="text-base" style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                To become the leading platform for finance education globally, known for producing industry-ready professionals 
                who drive innovation and excellence in financial markets. We envision a future where quality finance education 
                is accessible to everyone, regardless of their background.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="section">
        <div className="container">
          <h2 className="heading-secondary text-center mb-2xl">Our Core Values</h2>
          
          <div className="grid grid-2 gap-xl">
            {values.map((value, index) => (
              <div key={index} className="flex gap-lg items-start" data-testid={`value-${index}`}>
                <div style={{ width: '80px', height: '80px', background: 'var(--primary)', borderRadius: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontSize: '2rem', color: 'white' }}>{value.icon}</span>
                </div>
                <div>
                  <h3 className="heading-tertiary mb-md" style={{ fontSize: '1.25rem' }}>{value.title}</h3>
                  <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Journey */}
      <section className="section" style={{ background: 'var(--surface)' }}>
        <div className="container">
          <h2 className="heading-secondary text-center mb-2xl">Our Journey</h2>
          
          <div className="timeline">
            {milestones.map((milestone, index) => (
              <div key={index} className="timeline-item" data-testid={`milestone-${index}`}>
                <div className="timeline-year">{milestone.year}</div>
                <div className="timeline-content">
                  <h3 className="heading-tertiary mb-sm" style={{ fontSize: '1.125rem' }}>{milestone.title}</h3>
                  <p style={{ color: 'var(--text-secondary)' }}>{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="section">
        <div className="container">
          <h2 className="heading-secondary text-center mb-2xl">Leadership Team</h2>
          
          <div className="grid grid-3 gap-xl">
            {team.map((member, index) => (
              <div key={index} className="card text-center" data-testid={`team-member-${index}`}>
                <img 
                  src={member.image}
                  alt={member.name}
                  style={{ width: '120px', height: '120px', borderRadius: '50%', objectFit: 'cover', margin: '0 auto 1.5rem' }}
                />
                
                <h3 className="heading-tertiary mb-xs" style={{ fontSize: '1.25rem' }}>{member.name}</h3>
                <div className="text-sm font-semibold mb-xs" style={{ color: 'var(--secondary)' }}>{member.role}</div>
                <div className="text-sm mb-md" style={{ color: 'var(--text-secondary)' }}>{member.background}</div>
                
                <div className="text-sm" style={{ background: 'var(--surface)', padding: '0.5rem 1rem', borderRadius: '1rem', display: 'inline-block' }}>
                  {member.expertise}
                  {member.linkedin && (
  <div style={{ marginTop: "10px" }}>
    <a
      href={member.linkedin}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: "var(--secondary)",
        fontWeight: 600,
        fontSize: "0.9rem",
        textDecoration: "none"
      }}
    >
      in
    </a>
  </div>
)}

                  
                </div>
              </div>
            ))}
            
          </div>
        </div>
      </section>

      {/* Why Choose Intlrr */}
      <section className="section" style={{ background: 'var(--surface)' }}>
        <div className="container">
          <h2 className="heading-secondary text-center mb-2xl">Why Choose Intlrr?</h2>
          
          <div className="grid grid-3 gap-lg">
            <div className="text-center">
              <div className="stat-number" style={{ color: 'var(--secondary)' }}>95%</div>
              <div className="stat-label">Top-Rated Feedback</div>
            </div>
            
            <div className="text-center">
              <div className="stat-number" style={{ color: 'var(--secondary)' }}>10,000+</div>
              <div className="stat-label">Students Trained</div>
            </div>
            
            <div className="text-center">
              <div className="stat-number" style={{ color: 'var(--secondary)' }}>50+</div>
              <div className="stat-label">Industry Partners</div>
            </div>
          </div>
          
          <div className="text-center mt-2xl">
            <p className="text-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Our success is measured by your success. With industry-leading placement conversion rates, comprehensive curriculum, 
              and expert mentorship, we provide everything you need to launch a successful career in finance.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section" style={{ background: 'var(--primary)', color: 'white' }}>
        <div className="container text-center">
          <h2 className="heading-secondary mb-md" style={{ color: 'white' }}>
            Ready to Transform Your Career?
          </h2>
          <p className="text-large mb-xl" style={{ color: 'rgba(255, 255, 255, 0.9)', maxWidth: '600px', margin: '0 auto 2rem' }}>
            Join hundreds of professionals who have successfully launched their finance careers with Intlrr's expert guidance and industry connections.
          </p>
          <div className="flex gap-md justify-center">
            <Link to="/programs" className="btn btn-secondary btn-large" data-testid="start-journey-btn">
              Start Your Journey
            </Link>
            <Link to="/contact" className="btn btn-outline btn-large" style={{ color: 'white', borderColor: 'white' }} data-testid="learn-more-about-btn">
              Learn More
            </Link>
          </div>
        </div>
      </section>

      <style jsx>{`
        .timeline {
          position: relative;
          max-width: 800px;
          margin: 0 auto;
        }

        .timeline::before {
          content: '';
          position: absolute;
          left: 50%;
          top: 0;
          bottom: 0;
          width: 2px;
          background: var(--border);
          transform: translateX(-50%);
        }

        .timeline-item {
          display: flex;
          align-items: center;
          margin-bottom: 3rem;
          position: relative;
        }

        .timeline-item:nth-child(odd) {
          flex-direction: row;
        }

        .timeline-item:nth-child(even) {
          flex-direction: row-reverse;
        }

        .timeline-year {
          width: 120px;
          height: 120px;
          background: var(--secondary);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.25rem;
          position: relative;
          z-index: 1;
        }

        .timeline-content {
          flex: 1;
          padding: 0 2rem;
          max-width: 350px;
        }

        .timeline-item:nth-child(odd) .timeline-content {
          text-align: right;
        }

        .timeline-item:nth-child(even) .timeline-content {
          text-align: left;
        }

        @media (max-width: 768px) {
          .timeline::before {
            left: 60px;
          }

          .timeline-item {
            flex-direction: row !important;
          }

          .timeline-year {
            width: 80px;
            height: 80px;
            font-size: 1rem;
            position: absolute;
            left: 20px;
          }

          .timeline-content {
            margin-left: 120px;
            text-align: left !important;
            padding: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default About;